import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import {
  getRegistrationAdmin,
  RegistrationAdminData,
} from '@sportninja/common/services/registration/getRegistrationAdmin'

type UseRegistrationAdminOptions = {
  scheduleId: string
  /**
   * The modal-open flag. Gating on this is what gives us the "fetch when the
   * user opens Registration Setup" behaviour without a separate effect — the
   * query simply doesn't run while the modal is closed.
   */
  enabled: boolean
}

type UseRegistrationAdminResult = {
  data: RegistrationAdminData | null
  isLoading: boolean
  error: Error | null
  refetch: () => void
}

export const useRegistrationAdmin = ({
  scheduleId,
  enabled,
}: UseRegistrationAdminOptions): UseRegistrationAdminResult => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.registrationAdmin.bySchedule(scheduleId),
    queryFn: ({ signal }) => getRegistrationAdmin({ scheduleId, signal }),
    enabled: enabled && !!scheduleId,
    // The modal is re-opened often; the payload only changes on save (which
    // refetches explicitly), so a short stale window avoids a refetch storm
    // without ever serving genuinely outdated data.
    staleTime: 30_000,
  })

  return {
    data: data ?? null,
    isLoading,
    error: (error as Error) ?? null,
    refetch,
  }
}
